import { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { CTABanner } from "../../Components/PortfolioBlocks";
import { FaTimes } from "react-icons/fa";
import BrandButton from "../../Components/BrandButton";

const PortfolioAIAgents = () => {
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (audio && audio.duration) {
        const percent = (audio.currentTime / audio.duration) * 100;
        setProgress(percent);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setIsEnded(true);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;

    if (isEnded) {
      audio.currentTime = 0;
      audio.play();
      setIsEnded(false);
      setIsPlaying(true);
    } else if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const bar = progressBarRef.current;
    const rect = bar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const clickPercent = clickX / width;
    if (audio && audio.duration) {
      audio.currentTime = clickPercent * audio.duration;
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  const fadeUp2 = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay },
    }),
  };

  const headingRef = useRef(null);
  const paragraphRef = useRef(null);

  const headingControls = useAnimation();
  const paragraphControls = useAnimation();

  const isHeadingInView = useInView(headingRef, { once: true, amount: 0.4 });
  const isParagraphInView = useInView(paragraphRef, {
    once: true,
    amount: 0.4,
  });

  useEffect(() => {
    if (isHeadingInView) {
      headingControls.start("visible");
    }
  }, [isHeadingInView]);

  useEffect(() => {
    if (isParagraphInView) {
      paragraphControls.start("visible");
    }
  }, [isParagraphInView]);

  const imageFrames = [
    "/call1.jpg",
    "/call2.jpg",
    "/call3.jpg",
    "/call4.jpg",
    "/call5.jpg",
    "/ava.jpg",
    "/ava2.jpg",
    "/ava3.jpg",
    "/ava4.jpg",
    "/ava5.jpg",
  ];

  const [index, setIndex] = useState(0);
  const platformRef = useRef(null);
  const width = 1050;

  const [active, setActive] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    setZoomed(false);
    if (!active) return undefined;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active]);

  const next = () => {
    if (index < imageFrames.length - 1) setIndex(index + 1);
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const goTo = (target) => {
    if (target === "end") setIndex(imageFrames.length - 1);
    else if (!isNaN(target)) setIndex(Number(target));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index]);

  // useEffect(() => {
  //   if (platformRef.current) {
  //     platformRef.current.style.right = `${index * width}px`;
  //   }
  // }, [index]);

  return (
    <section className="text-black">
      <div className="mt-20 font-sans overflow-x-hidden relative ">
        <section className="relative z-10 py-16 md:py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center text-center lg:text-left">
            <div className="space-y-8">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={0}
              >
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#8a4afa1c]  text-primary-dark font-medium">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8B4AFA] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-dark"></span>
                  </span>
                  <span className="text-[#8B4AFA]">AI Agents</span>
                </div>
              </motion.div>

             <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight text-gray-900">
                Transform your business{" "}
                <span className="text-[#8B4AFA]">with AI</span>
              </h1>

             <motion.p
              className="text-lg md:text-xl text-gray-600 max-w-2xl mt-2"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
            >
              Imagine an AI-powered voice agent that automates routine tasks
              like scheduling appointments, confirming leads, sending meeting
              reminders, and seamlessly transferring calls to live agents. Our
              intelligent voice solutions help your business
              <span className="ml-2 font-semibold">
                save time, reduce costs, and boost operational efficiency.
              </span>
            </motion.p>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={3}
              >
                <audio ref={audioRef} src="/rec.mp3" preload="none" />
                <div className="flex-1 flex flex-col sm:flex-row items-center  space-y-4 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center">
                    <div className="flex space-x-3">
                      <button
                        onClick={togglePlay}
                        className="rounded-xl bg-[#8B4AFA] w-10 h-10 flex items-center justify-center pl-0.5 text-white"
                      >
                        {isEnded ? (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 .49-5.27L1 10" />
                          </svg>
                        ) : isPlaying ? (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div
                    className="relative w-full min-w-0 cursor-pointer"
                    onClick={handleSeek}
                    ref={progressBarRef}
                  >
                    <div className="bg-[#8a4afa25] h-2 w-full rounded-lg"></div>
                    <div
                      className="bg-[#8B4AFA] h-2 rounded-lg absolute top-0 left-0 transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              </motion.div>
              <motion.p
                className="text-sm md:text-md text-gray-600 max-w-2xl "
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={4}
              >
                Listen to our bot in action and discover how it can
                revolutionize your customer experience.
              </motion.p>

              <motion.div
                className="flex items-center pt-2 space-x-4"
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={5}
              >
                <BrandButton
                    as="a"
                    href="https://agent.mkaitechnology.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mk-cta-button--full"
                  >
                    Talk With AI Agent Now
                  </BrandButton>
              </motion.div>
            </div>

            <div className="relative">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={0}
                className="relative z-10 aspect-square w-full h-auto rounded-3xl overflow-hidden shadow-2xl bg-[#f5efff] border border-gray-100"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-light/10 to-secondary-light/10"></div>

                <div className="absolute inset-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm p-6 flex flex-col">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-3 h-3 rounded-full bg-[#8B4AFA]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#8B4AFA]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#8B4AFA]"></div>
                    <div className="flex-1 h-4 rounded-full bg-[#8a4afa36]"></div>
                  </div>

                  <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-[#8a4afa1f] text-sm text-gray-700 p-3 rounded-xl shadow-inner max-w-[70%]">
                      Hello! I'm your AI Assistant. How can I help you today?
                    </div>
                  </div>

                 <div className="flex-1 min-h-0 overflow-y-auto flex flex-col space-y-3 text-sm text-gray-700 pr-1 scrollbar-thin scrollbar-thumb-[#8B4AFA]/30">
                    <div className="self-end bg-[#8B4AFA] text-white rounded-xl px-4 py-2 max-w-[75%]">
                      Can you schedule a dentist appointment for me tomorrow at
                      3 PM?
                    </div>
                    <div className="self-start bg-[#8a4afa14] rounded-xl px-4 py-2 max-w-[75%]">
                      🗓️ Sure! I've booked your dentist appointment for tomorrow
                      at 3 PM.
                    </div>
                    <div className="self-end bg-[#8B4AFA] text-white rounded-xl px-4 py-2 max-w-[75%]">
                      Also, please remind me 30 minutes before it starts.
                    </div>
                    <div className="self-start bg-[#8a4afa14] rounded-xl px-4 py-2 max-w-[75%]">
                      ✅ Reminder set for 2:30 PM. You’ll get a voice
                      notification.
                    </div>
                    <div className="self-end bg-[#8B4AFA] text-white rounded-xl px-4 py-2 max-w-[75%]">
                      What’s the clinic address?
                    </div>
                    <div className="self-start bg-[#8a4afa14] rounded-xl px-4 py-2 max-w-[75%]">
                      🏥 It's SmileCare Dental, 123 Main Street, Downtown.
                    </div>
                    <div className="self-end bg-[#8B4AFA] text-white rounded-xl px-4 py-2 max-w-[75%]">
                      Thanks! You’re amazing.
                    </div>

                    <div className="self-start bg-[#8a4afa14] rounded-xl px-4 py-2 max-w-[75%]">
                      You're welcome! 😊
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={6}
               className="absolute -bottom-6 -left-6 w-32 h-32 rounded-2xl bg-[#8a4afa13] backdrop-blur-sm   shadow-lg z-0"
              />

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={4}
               className="absolute -top-6 -right-6 w-40 h-40 rounded-2xl bg-[#8a4afa27] backdrop-blur-sm   shadow-lg z-0"
              />

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={7}
                className="absolute top-14 -left-14 w-24 h-24 rounded-2xl bg-[#8a4afa18] backdrop-blur-sm   shadow-lg z-0"
              />

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={8}
                className="absolute bottom-20 -right-20 w-32 h-32 rounded-2xl bg-[#8a4afa22] backdrop-blur-sm   shadow-lg z-0"
              />
            </div>
          </div>
        </section>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center lg:pt-32">
        <motion.p
          className="mx-auto -mt-4 max-w-2xl text-md tracking-widest text-slate-700 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          We Provide
          <span className="border-b border-dotted border-slate-300 ml-2">
            Both
          </span>
        </motion.p>

        <motion.h2
          className="mx-auto mt-4 max-w-4xl font-display text-5xl font-semibold tracking-tight text-slate-900 sm:text-7xl leading-tight"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
          viewport={{ once: true }}
        >
          <span className="inline-block"> Voice AI Integration Options</span>
        </motion.h2>

        <motion.p
          className="mx-auto mt-9 max-w-2xl text-lg tracking-tight text-slate-600 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.6 }}
          viewport={{ once: true }}
        >
          Compare the benefits, costs, and timelines of using pre-built voice
          platforms versus building your own custom AI voice assistant tailored
          to your business needs.
        </motion.p>
        <div className=" py-10 grid grid-cols-1 text-start md:grid-cols-2 gap-8">
            <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ y: -6, transition: { duration: 0.2, delay: 0 } }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
            viewport={{ once: true }}
            className="relative p-8 border border-gray-200 rounded-2xl shadow-2xl flex flex-col transition-colors duration-300 hover:border-emerald-300"
          >
            <h3 className="text-2xl font-semibold text-green-500 mb-3">
              Pre-Built Solution
            </h3>
            <p className="text-gray-700 mb-4">
              Leverage ready-made platforms like <strong>Vapi</strong>,{" "}
              <strong>Retell AI</strong> for voice, and <strong>Twilio</strong>{" "}
              for telephony.
            </p>
            <ul role="list" class="mt-1 space-y-6">
              <li class="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  class="flex-shrink-0 w-6 h-6 text-emerald-500"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span class="ml-3 ">Fast Setup</span>
              </li>
              <li class="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  class="flex-shrink-0 w-6 h-6 text-emerald-500"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span class="ml-3 ">Lower Development Cost</span>
              </li>
              <li class="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  class="flex-shrink-0 w-6 h-6 text-yellow-500"
                  aria-hidden="true"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12" y2="17"></line>
                </svg>
                <span class="ml-3 ">Higher Monthly Usage Fees</span>
              </li>
            </ul>
            <div className="overflow-x-auto mt-6">
              <table className="min-w-full text-sm text-left text-gray-800 bg-green-50 rounded-lg">
                <thead className="text-xs uppercase text-green-700 bg-green-100">
                  <tr>
                    <th scope="col" className="px-4 py-3">
                      Service
                    </th>
                    <th scope="col" className="px-4 py-3 text-end">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-green-200">
                    <td className="px-4 py-2 font-medium">Vapi</td>
                    <td className="px-4 py-2 text-end">$0.14/min</td>
                  </tr>
                  <tr className="border-t border-green-200">
                    <td className="px-4 py-2 font-medium">Twilio</td>
                    <td className="px-4 py-2 text-end">$0.0085/min</td>
                  </tr>
                  <tr className="border-t border-green-200">
                    <td className="px-4 py-2 font-medium">Total</td>
                    <td className="px-4 py-2 text-end">~$0.1485/min</td>
                  </tr>
                  <tr className="border-t border-green-200">
                    <td className="px-4 py-2 font-medium">Development Cost</td>
                    <td className="px-4 py-2 text-end">Low</td>
                  </tr>
                  <tr className="border-t border-green-200">
                    <td className="px-4 py-2 font-medium">
                      Estimated Timeline
                    </td>
                    <td className="px-4 py-2 text-end">1–2 weeks</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

            <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ y: -6, transition: { duration: 0.2, delay: 0 } }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 1.2 }}
            viewport={{ once: true }}
            className="relative p-8 border border-gray-200 rounded-2xl shadow-2xl flex flex-col transition-colors duration-300 hover:border-[#8B4AFA]"
          >
            <p className="absolute top-0 py-1.5 px-4 bg-[#8B4AFA] text-white rounded-full text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2">
              Popular
            </p>
            <h3 className="text-2xl font-semibold text-[#8B4AFA] mb-3">
              Custom Solution
            </h3>
            <p className="text-gray-700 mb-4">
              Build a fully tailored AI voice assistant using{" "}
              <strong>OpenAI</strong> for voice intelligence and{" "}
              <strong>Twilio</strong> for telephony.
            </p>

            <ul role="list" className="mt-1 space-y-6">
              <li className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 w-6 h-6 text-[#8B4AFA]"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span className="ml-3">Extremely Low Ongoing Costs</span>
              </li>

              <li className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 w-6 h-6 text-[#8B4AFA]"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span className="ml-3">Full Customization</span>
              </li>

              <li className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 w-6 h-6 text-yellow-500"
                  aria-hidden="true"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12" y2="17"></line>
                </svg>
                <span className="ml-3">Higher Upfront Development Cost</span>
              </li>
            </ul>

            {/* Cost Breakdown Table */}
            <div className="overflow-x-auto mt-6">
              <table className="min-w-full text-sm text-left text-gray-800  bg-purple-50 rounded-lg">
                <thead className="text-xs uppercase text-purple-700 bg-purple-100">
                  <tr>
                    <th scope="col" className="px-4 py-3">
                      Service
                    </th>
                    <th scope="col" className="px-4 py-3 text-end">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-purple-200">
                    <td className="px-4 py-2 font-medium">OpenAI</td>
                    <td className="px-4 py-2 text-end">~$0.01/min</td>
                  </tr>
                  <tr className="border-t border-purple-200">
                    <td className="px-4 py-2 font-medium">Twilio</td>
                    <td className="px-4 py-2 text-end">$0.0085/min</td>
                  </tr>
                  <tr className="border-t border-purple-200">
                    <td className="px-4 py-2 font-medium">Total</td>
                    <td className="px-4 py-2 text-end">~$0.0185/min</td>
                  </tr>
                  <tr className="border-t border-purple-200">
                    <td className="px-4 py-2 font-medium">Development Cost</td>
                    <td className="px-4 py-2 text-end">High (one-time)</td>
                  </tr>
                  <tr className="border-t border-purple-200">
                    <td className="px-4 py-2 font-medium">
                      Estimated Timeline
                    </td>
                    <td className="px-4 py-2 text-end">3–4 weeks</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="flex justify-center items-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center lg:pt-20 ">
          <motion.p
            className="mx-auto -mt-4 max-w-2xl text-md tracking-widest text-slate-700 sm:mt-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            Our
            <span className="border-b border-dotted border-slate-300 ml-2">
              Work
            </span>
          </motion.p>

          <motion.h2
            className="mx-auto mt-4 max-w-4xl font-display text-3xl sm:text-3xl md:text-5xl font-semibold tracking-tight text-slate-900  leading-tight"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
            viewport={{ once: true }}
          >
            <span className="inline-block">Dashboards We Created </span>
          </motion.h2>

          <motion.p
            className="mx-auto mt-9 max-w-2xl text-lg tracking-tight text-slate-600 sm:mt-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.6 }}
            viewport={{ once: true }}
          >
            Explore a showcase of powerful, production-ready admin panels and
            dashboards we've developed - each tailored to deliver performance,
            usability, and clean design.
          </motion.p>

                   <motion.div
            className="gallery w-full rounded mx-auto m-5 hidden md:block"
            style={{ maxWidth: `${width}px` }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
            viewport={{ once: true }}
          >
            <div className="content-area w-full overflow-hidden">
              <div
                className="platform flex transition-transform duration-300"
                style={{
                  width: `${imageFrames.length * 100}%`,
                  transform: `translateX(-${(index * 100) / imageFrames.length}%)`,
                }}
              >
                {imageFrames.map((src, i) => (
                  <div
                    key={i}
                    className="each-frame flex-none flex items-center justify-center"
                    style={{ width: `${100 / imageFrames.length}%` }}
                  >
                    <button
                      type="button"
                      onClick={() => setActive({ src, caption: `Frame ${i + 1}` })}
                      aria-label={`Enlarge frame ${i + 1}`}
                      className="block w-full cursor-zoom-in"
                    >
                     <img
                        src={src}
                        width="1200"
                        height="600"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto border-2 rounded-2xl object-contain"
                        alt={`Frame ${i + 1}`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="top flex p-2 items-center justify-center ">
              <div className="buttons  flex gap-3  text-gray-600 mr-1">
                <motion.svg
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 1.1 }}
                  onClick={prev}
                  className="w-10 rounded-lg bg-[#8B4AFA] p-2 cursor-pointer"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="#fff"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </motion.svg>
                <motion.svg
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 1.1 }}
                  onClick={next}
                  className="w-10 rounded-lg bg-[#8B4AFA] p-2 cursor-pointer"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="#fff"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </motion.svg>
              </div>
            </div>
          </motion.div>
          <div className="flex flex-col mt-6 md:hidden">
            {imageFrames.map((src, i) => (
              <div
                key={i}
                className="w-full flex items-center justify-center mb-4"
              >
                 <button
                  type="button"
                  onClick={() => setActive({ src, caption: `Frame ${i + 1}` })}
                  className="block w-full"
                >
                  <img
                    src={src}
                    width="1200"
                    height="600"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto border-2 rounded-2xl object-contain"
                    alt={`Frame ${i + 1}`}
                  />
                </button>
              </div>
            ))}
                    </div>
        </div>
      </div>

      <CTABanner
        title="Ready to put an AI voice agent to work?"
        text="Tell us what you want automated, from booking appointments to answering calls, and we'll show you a free demo."
        buttonLabel="Get a Free Demo"
        color="#7C3AED"
               hoverBg="#f5f3ff"
      />

      {active && (
        <motion.div
          className="fixed inset-0 z-[1000] overflow-auto bg-black/80 p-4"
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
          aria-label={active.caption}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-label="Close"
            className="fixed right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg"
          >
            <FaTimes />
          </button>
          <div className="flex min-h-full">
            <img
              src={active.src}
              alt={active.caption}
              onClick={(e) => {
                e.stopPropagation();
                setZoomed((z) => !z);
              }}
              className={`m-auto rounded-lg bg-white shadow-2xl ${
                zoomed
                  ? "max-w-none w-[1800px] cursor-zoom-out"
                  : "max-h-[90vh] max-w-full object-contain cursor-zoom-in"
              }`}
            />
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default PortfolioAIAgents;

